import type { DailyCommuteLocation } from "@/types/dailyCommuteLocation";

interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface GooglePlaceDetailsResult {
  place_id: string;
  name?: string;
  formatted_address?: string;
  geometry?: {
    location?: {
      lat?: number;
      lng?: number;
    };
  };
  address_components?: GoogleAddressComponent[];
  types?: string[];
}

function getAddressComponent(
  components: GoogleAddressComponent[],
  type: string,
  nameType: "long_name" | "short_name" = "long_name"
): string | undefined {
  const match = components.find((component) => component.types.includes(type));
  return match?.[nameType];
}

export function normalizeGooglePlace(
  result: GooglePlaceDetailsResult
): DailyCommuteLocation {
  const components = result.address_components ?? [];
  const lat = result.geometry?.location?.lat ?? 0;
  const lng = result.geometry?.location?.lng ?? 0;
  const name = result.name?.trim() ?? "";
  const formattedAddress = result.formatted_address?.trim() ?? "";

  return {
    placeId: result.place_id,
    name: name || formattedAddress,
    address: formattedAddress || name,
    lat,
    lng,
    locality:
      getAddressComponent(components, "locality") ||
      getAddressComponent(components, "sublocality") ||
      getAddressComponent(components, "administrative_area_level_2"),
    state: getAddressComponent(components, "administrative_area_level_1"),
    country: getAddressComponent(components, "country"),
    countryCode: getAddressComponent(components, "country", "short_name"),
  };
}

export function toLocationDisplayLabel(location: DailyCommuteLocation): string {
  return location.name || location.address;
}

export function toLocationFullAddress(location: DailyCommuteLocation): string {
  return location.address?.trim() || location.name?.trim() || "";
}
